import React, { useState, useEffect } from "react";
import { Button, Grid, Typography, Box, useTheme } from "@mui/material";
import { useDepthChart } from "../../../../contexts/DepthChartContext";
import { useLogger } from "../../../../contexts/LoggerContext";
import {
  calculateKickoffYardage,
  calculateReturnYardage,
} from "../../../../helpers/helpers";
import { RosterPlayer } from "../../../../types/LogTypes";
import { PlayerSelection } from "../Components/PlayerSelection";
import { YardlineInput } from "../Components/YardlineInput";

export const PuntLog = () => {
  const [selectedKicker, setSelectedKicker] = useState<RosterPlayer | null>(
    null
  );
  const [jerseyNumber, setJerseyNumber] = useState<string>("");
  const [ballCaughtOn, setBallCaughtOn] = useState<number>(0);
  const [ballReturnedTo, setBallReturnedTo] = useState<number>(-20);
  const [touchback, setTouchback] = useState<boolean>(false);
  const [kickoffFrom, setKickoffFrom] = useState<number>(40);
  const returnYardage = calculateReturnYardage(ballCaughtOn, ballReturnedTo);
  const kickoffYardage = calculateKickoffYardage(kickoffFrom, ballCaughtOn);
  const { punters } = useDepthChart();
  const {
    gameSituation,
    touchdown,
    safety,
    setTouchdown,
    setSafety,
    setGameSituation,
    setCurrentPlayLog,
    clearSelections,
  } = useLogger();

  const theme = useTheme();

  const handleJerseyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setJerseyNumber(value);
    }
  };

  const handleTouchdown = () => {
    setTouchdown(!touchdown);
    if (!touchdown) {
      setGameSituation({
        ...gameSituation,
        opp_score: gameSituation.opp_score + 6,
      });
    } else {
      setGameSituation({
        ...gameSituation,
        opp_score: gameSituation.opp_score - 6,
      });
    }
  };

  const handleTouchback = () => {
    setTouchback(!touchback);
    if (!touchback) {
      setGameSituation({
        ...gameSituation,
        club_possession: false,
      });
    } else {
      setGameSituation({
        ...gameSituation,
        club_possession: true,
      });
    }
  };

  const handleKickerSelection = (player: RosterPlayer) => {
    setSelectedKicker(player);
    setJerseyNumber(player.jersey_number?.toString() || "");
  };

  useEffect(() => {
    if (selectedKicker) {
      setCurrentPlayLog({
        punter_roster_player: selectedKicker,
        punt_from: kickoffFrom,
        punt_to: ballCaughtOn,
        punt_yardage: kickoffYardage,
        return_to_yardline: ballReturnedTo,
        return_yardage: returnYardage,
      });
    }
  }, [selectedKicker, kickoffFrom, ballCaughtOn, ballReturnedTo]);

  useEffect(() => {
    if (clearSelections) {
      setSelectedKicker(null);
      setJerseyNumber("");
      setBallCaughtOn(0);
      setBallReturnedTo(-20);
      setTouchback(false);
      setTouchdown(false);
      setKickoffFrom(40);
    }
  }, [clearSelections]);

  return (
    <Box>
      <Grid container>
        {/* Punter */}
        <Grid item xs={2}>
          <PlayerSelection
            title="Punter"
            players={punters}
            selectedPlayer={selectedKicker}
            jerseyNumber={jerseyNumber}
            maxDisplayedPlayers={1}
            handlePlayerSelection={handleKickerSelection}
            handleJerseyNumberChange={handleJerseyNumberChange}
          />
        </Grid>

        {/* Return Yardage */}
        <Grid item xs={8}>
          <Box sx={{ flex: 1 }}></Box>

          <YardlineInput
            value={ballCaughtOn}
            onChange={setBallCaughtOn}
            label="Ball Caught On"
          />
          <YardlineInput
            value={ballReturnedTo}
            onChange={setBallReturnedTo}
            label="Ball Returned To"
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <Button
              variant={touchback ? "contained" : "outlined"}
              color={touchback ? "error" : "primary"}
              sx={{ minWidth: 150, fontWeight: touchback ? "bold" : "normal" }}
              onClick={handleTouchback}
            >
              Touchback
            </Button>
            <Button
              variant={touchdown ? "contained" : "outlined"}
              color={touchdown ? "success" : "primary"}
              sx={{ minWidth: 150, fontWeight: touchdown ? "bold" : "normal" }}
              onClick={handleTouchdown}
            >
              Touchdown
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" align="center" gutterBottom>
            Return Yardage: {returnYardage} yards
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};
